import { calloutInfoAt, calloutMask, callouts, CALLOUT_CELLS, type Callout, type CalloutEdit } from "./callouts.ts";
import type { Level, MapId } from "./catalog.ts";
import { levelAt } from "./coords.ts";

/**
 * Which callouts touch which: "where I play" is my spot's callout and the ones next to
 * it (the "From my spot" filter, 0.27.0). Worked out from the callouts' cells on the
 * radar grid: two callouts are neighbours when enough of their cells meet, across a gap
 * of at most NEAR cells (a door frame, a thin wall between two places).
 *
 * A spot is written "<floor>|<callout key>", as Team › Who plays where stores it
 * ("upper|base:Jungle#0").
 */

/** How far apart two callouts' cells may be and still count as touching (grid cells, ~8 radar px each). */
const NEAR = 2;
/** How many cells must meet: one corner brushing another is not a way through. */
const MIN_CONTACTS = 3;

/**
 * What the grid gets wrong, by name ("<floor>|<callout name>"): places that connect
 * without touching on the radar (stairs between floors, drops), and places that touch
 * through a wall. Each pair works both ways.
 */
export interface AdjacencyOverrides {
  add?: [string, string][];
  remove?: [string, string][];
}

export const ADJACENCY_OVERRIDES: Partial<Record<MapId, AdjacencyOverrides>> = {
  // Nuke's two floors meet through the ramp, the vents and the silo ladders.
  de_nuke: {
    add: [
      ["upper|Ramp", "lower|Ramp"],
      ["upper|A Site", "lower|Vents"],
      ["upper|Hut", "lower|Secret"],
    ],
  },
  // Mirage: Jungle opens onto Connector, and Ticket Booth onto CT Spawn, through narrow doors the grid misses.
  de_mirage: {
    add: [
      ["upper|Jungle", "upper|Connector"],
      ["upper|Ticket Booth", "upper|CT Spawn"],
    ],
  },
};

const spotOf = (level: Level, c: Callout) => `${level}|${c.key}`;
const levelOf = (spot: string): Level => (spot.startsWith("lower|") ? "lower" : "upper");
const keyOf = (spot: string) => spot.slice(spot.indexOf("|") + 1);

/** Each grid cell's callout (index into `list`), -1 for none: what the floor is made of. */
function owners(map: MapId, level: Level, edits: readonly CalloutEdit[]): { list: Callout[]; cells: Int16Array } {
  const list = callouts(map, level, edits);
  const cells = new Int16Array(CALLOUT_CELLS * CALLOUT_CELLS).fill(-1);
  // Valve's places first; then drawn boxes, the biggest first so the smallest wins where they overlap.
  const order = list.map((c, i) => ({ c, i })).sort((a, b) => Number(!!a.c.zone) - Number(!!b.c.zone) || boxSize(b.c) - boxSize(a.c));
  for (const { c, i } of order) {
    if (c.hidden) continue;
    const mask = calloutMask(map, level, c.key, edits);
    for (let k = 0; k < mask.length; k++) if (mask[k]) cells[k] = i;
  }
  return { list, cells };
}

const boxSize = (c: Callout) => (c.zone ? (c.zone.u1 - c.zone.u0) * (c.zone.v1 - c.zone.v0) : 0);

const cache = new Map<string, Map<string, Set<string>>>();

/** Every neighbour pair on a map floor, from the grid alone (no overrides). */
function gridPairs(map: MapId, level: Level, edits: readonly CalloutEdit[]): Map<string, Set<string>> {
  const key = `${map}:${level}:${edits.length ? JSON.stringify(edits) : ""}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const { list, cells } = owners(map, level, edits);
  const n = CALLOUT_CELLS;
  const contacts = new Map<string, number>();
  for (let y = 0; y < n; y++)
    for (let x = 0; x < n; x++) {
      const a = cells[y * n + x];
      if (a < 0) continue;
      // Look right and down only (each pair once), within NEAR cells.
      for (let dy = 0; dy <= NEAR; dy++)
        for (let dx = -NEAR; dx <= NEAR; dx++) {
          if (dy === 0 && dx <= 0) continue;
          const x2 = x + dx;
          const y2 = y + dy;
          if (x2 < 0 || x2 >= n || y2 >= n) continue;
          const b = cells[y2 * n + x2];
          if (b < 0 || b === a) continue;
          const pair = a < b ? `${a},${b}` : `${b},${a}`;
          contacts.set(pair, (contacts.get(pair) ?? 0) + 1);
        }
    }
  const out = new Map<string, Set<string>>();
  const link = (p: string, q: string) => {
    if (!out.has(p)) out.set(p, new Set());
    out.get(p)!.add(q);
  };
  for (const [pair, count] of contacts) {
    if (count < MIN_CONTACTS) continue;
    const [a, b] = pair.split(",").map(Number);
    link(spotOf(level, list[a]), spotOf(level, list[b]));
    link(spotOf(level, list[b]), spotOf(level, list[a]));
  }
  cache.set(key, out);
  return out;
}

/** The spots on a map named so ("upper|Jungle"): every label of that name on that floor. */
function spotsNamed(map: MapId, named: string, edits: readonly CalloutEdit[]): string[] {
  const level = levelOf(named);
  const name = keyOf(named);
  return callouts(map, level, edits)
    .filter((c) => !c.hidden && (c.name === name || c.base === name))
    .map((c) => spotOf(level, c));
}

/**
 * The callouts next to a spot ("upper|base:A Site#0"), as spots, the spot itself left
 * out: those whose cells meet its cells on the radar, with the map's overrides applied.
 */
export function adjacentCallouts(map: MapId, spot: string, edits: readonly CalloutEdit[] = [], overrides: AdjacencyOverrides | undefined = ADJACENCY_OVERRIDES[map]): string[] {
  const level = levelOf(spot);
  const out = new Set(gridPairs(map, level, edits).get(spot) ?? []);
  const me = new Set([spot]);
  const touches = (pair: [string, string]) => {
    const [a, b] = pair.map((p) => spotsNamed(map, p, edits));
    if (a.some((s) => me.has(s))) return b;
    if (b.some((s) => me.has(s))) return a;
    return [];
  };
  for (const pair of overrides?.add ?? []) for (const s of touches(pair)) out.add(s);
  for (const pair of overrides?.remove ?? []) for (const s of touches(pair)) out.delete(s);
  out.delete(spot);
  return [...out].sort();
}

/** Where a player plays, as callouts: their spots and every callout next to one of them. */
export function spotArea(map: MapId, spots: readonly string[], edits: readonly CalloutEdit[] = []): Set<string> {
  const area = new Set<string>();
  for (const s of spots) {
    if (!s) continue;
    area.add(s);
    for (const n of adjacentCallouts(map, s, edits)) area.add(n);
  }
  return area;
}

/** The spot a world position is in ("upper|base:Jungle#0"), as Who plays where writes it; null off the callouts. */
export function spotAt(map: MapId, x: number, y: number, z: number | null, edits: readonly CalloutEdit[] = []): string | null {
  const c = calloutInfoAt(map, x, y, z, edits);
  return c ? spotOf(levelAt(map, z), c) : null;
}
